import {
  DEFAULT_HOST_LOCALE,
  type HostLocale,
  SIGNUP_LANGUAGE_PARAM,
  signupLanguageFrom,
} from "@vidimo/shared";
import { isConnectPagePath } from "@/connect/origins";
import {
  ENGLISH_MESSAGES,
  type InterfaceMessages,
  interfaceMessages,
} from "./catalogs";
import { deviceLanguages } from "./device-languages";
import {
  type InterfaceLocaleSource,
  type LocaleSignal,
  type ResolvedInterfaceLocale,
  resolveSignedInLocale,
  resolveSignedOutLocale,
  type SignedOutSignals,
} from "./resolve-interface-locale";

/**
 * The interface language the app renders in, held outside React so the
 * provider at the root can be seeded before the root exists and fed from a
 * shell that remounts. Immutable: every change is a new object, and `locale`
 * and `messages` change in the same one, so no frame pairs a locale with
 * another's words.
 */
export interface InterfaceLocaleState {
  readonly automaticLocale: HostLocale;
  readonly locale: HostLocale;
  readonly messages: InterfaceMessages;
  readonly source: InterfaceLocaleSource;
}

/** What the signed-in shell knows about the host, as it learns it. */
export interface SignedInLocaleInputs {
  /**
   * `staff.displayLanguage` from the local row; null means Automatic,
   * undefined that the row has not loaded yet.
   */
  explicit: LocaleSignal;
  /** `user.signupLanguage` from the live session; undefined while unknown. */
  signup: LocaleSignal;
  /** Whose language this is, recorded beside it in the mirror. */
  userId: string;
}

/**
 * The device's record of the last language a signed-in host settled on, so
 * the next cold start's first frame, and the login screen after sign-out,
 * render in it. Written only when a signed-in resolution settles; sign-out
 * keeps it, as it keeps the theme.
 */
const MIRROR_STORAGE_KEY = "superhost.interface-locale.v1";

/** The routes whose `?lang` a marketing trial link fills in. */
const LINK_LANGUAGE_PATHS = new Set(["/signup", "/login"]);
const TRAILING_SLASHES_RE = /\/+$/;

type Listener = () => void;

type LinkSignals = Pick<SignedOutSignals, "hostLink" | "link">;

const NO_LINK: LinkSignals = { hostLink: null, link: null };

const INITIAL_STATE: InterfaceLocaleState = {
  automaticLocale: DEFAULT_HOST_LOCALE,
  locale: DEFAULT_HOST_LOCALE,
  messages: ENGLISH_MESSAGES,
  source: "default",
};

let state = INITIAL_STATE;
/** Latched once per page load, so it outlives the `?lang` it was read from. */
let links = NO_LINK;
let signedIn: SignedInLocaleInputs | null = null;
const listeners = new Set<Listener>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

interface MirrorEntry {
  locale: string;
  userId: string;
}

function readMirror(): MirrorEntry | null {
  try {
    const raw = window.localStorage.getItem(MIRROR_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      typeof parsed.locale !== "string" ||
      typeof parsed.userId !== "string"
    ) {
      return null;
    }
    return { locale: parsed.locale, userId: parsed.userId };
  } catch {
    // No storage (a private window, blocked site data, no window at all) or
    // a corrupt entry: the chain goes on to the device.
    return null;
  }
}

/**
 * Records a settled language, or forgets the record when the resolution only
 * fell back to English: a fallback says nothing about the host, and recorded
 * it would outrank a trial link's language on a device the app does not ship.
 */
function writeMirror(
  { locale, source }: ResolvedInterfaceLocale,
  userId: string
): void {
  try {
    if (source === "default") {
      window.localStorage.removeItem(MIRROR_STORAGE_KEY);
      return;
    }
    const entry = JSON.stringify({ locale, userId });
    if (window.localStorage.getItem(MIRROR_STORAGE_KEY) !== entry) {
      window.localStorage.setItem(MIRROR_STORAGE_KEY, entry);
    }
  } catch {
    // Storage unavailable: the next cold start falls back to the device.
  }
}

function setState(next: InterfaceLocaleState): void {
  const unchanged =
    next.locale === state.locale &&
    next.messages === state.messages &&
    next.source === state.source &&
    next.automaticLocale === state.automaticLocale;
  if (unchanged) {
    return;
  }
  state = next;
  // Here rather than in an effect, so the first frame of a non-English locale
  // is never painted under index.html's `lang="en"`.
  document.documentElement.lang = next.locale;
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Switches to a resolved locale, in this call, and records it in the mirror
 * when a signed-in host settled on it.
 */
function apply(
  resolved: ResolvedInterfaceLocale,
  mirrorFor: string | null
): void {
  setState({ ...resolved, messages: interfaceMessages(resolved.locale) });
  if (mirrorFor) {
    writeMirror(resolved, mirrorFor);
  }
}

function resolve(): void {
  const device = deviceLanguages();
  if (!signedIn) {
    const signedOut = resolveSignedOutLocale({
      device,
      ...links,
      mirror: readMirror()?.locale ?? null,
    });
    apply(signedOut, null);
    return;
  }
  const { explicit, signup, userId } = signedIn;
  const signedInResolution = resolveSignedInLocale({
    device,
    explicit,
    signup,
  });
  if (signedInResolution.pending) {
    // A known pick that no longer decides, while the screen shows a pick, is
    // one the host left: keeping it would ignore their choice. The best
    // reading so far stands in, and replaces the pick in the mirror too, so
    // neither the login screen after sign-out nor the next cold start brings
    // it back.
    const leftPick = explicit !== undefined && state.source === "explicit";
    if (leftPick) {
      apply(signedInResolution.provisional, userId);
      return;
    }
    // Nor is another host's language, left on screen by the device's mirror
    // for the login screen: it says nothing about the host now signed in, so
    // the best reading so far stands in, recorded nowhere until it settles.
    const heldForAnotherHost =
      state.source === "mirror" && readMirror()?.userId !== userId;
    if (heldForAnotherHost) {
      apply(signedInResolution.provisional, null);
      return;
    }
    // Otherwise keep what is on screen until the signal arrives: at most one
    // switch once it does, and never a downgrade on an offline cold start.
    return;
  }
  const { automaticLocale, locale, source } = signedInResolution;
  // A settled signed-in language supersedes the link's: after sign-out the
  // login screen stays in the language just used, which the mirror now holds.
  links = NO_LINK;
  apply({ automaticLocale, locale, source }, userId);
}

function linkSignalsOf({
  pathname,
  search,
}: Pick<Location, "pathname" | "search">): LinkSignals {
  const language = signupLanguageFrom(
    new URLSearchParams(search).get(SIGNUP_LANGUAGE_PARAM)
  );
  if (isConnectPagePath(pathname)) {
    return { hostLink: language, link: null };
  }
  const path = pathname.replace(TRAILING_SLASHES_RE, "");
  return LINK_LANGUAGE_PATHS.has(path)
    ? { hostLink: null, link: language }
    : NO_LINK;
}

/**
 * Resolves the first frame's language, synchronously, before the React root
 * is created: on a connect page the link's, then this device's last settled
 * language, else the device's, else the link's on `/signup` or `/login`, else
 * English. The link's is read here once and held for the page load, so it
 * survives the app replacing the URL after sign-up; it is never stored.
 */
export function seedInterfaceLocale(
  location: Pick<Location, "pathname" | "search">
): void {
  links = linkSignalsOf(location);
  resolve();
}

/**
 * Hands the store what the signed-in shell knows. Each call replaces the
 * last; a signal still `undefined` holds the language on screen.
 */
export function publishSignedInLocaleSignals(
  inputs: SignedInLocaleInputs
): void {
  signedIn = { ...inputs };
  resolve();
}

/**
 * Drops the signed-in signals when the signed-in shell unmounts (sign-out).
 * The mirror is kept, so the screen stays in the language just used.
 */
export function clearSignedInLocaleSignals(): void {
  if (signedIn === null) {
    return;
  }
  signedIn = null;
  resolve();
}

export function getInterfaceLocaleState(): InterfaceLocaleState {
  return state;
}

export function subscribeInterfaceLocale(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Back to the state before any seed. vitest's vmThreads pool shares a
 * worker's module graph across files, so a locale one file settled on would
 * otherwise be the next file's first frame.
 */
export function resetInterfaceLocaleStoreForTest(): void {
  links = NO_LINK;
  signedIn = null;
  setState(INITIAL_STATE);
}
